import axios from 'axios';
import CryptoJS from 'crypto-js';

const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    // 开发环境使用环境变量中的域名
    return process.env.VUE_APP_API_BASE_URL;
  } else {
    // 生产环境使用当前域名
    return `${window.location.origin}`;
  }
};

const apiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// AES 加密密钥
const ENCRYPTION_KEY = 'your-secret-key-here'; // 确保这个值与后端完全一致

// 加密函数
const encrypt = (text) => {
  return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
};

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default {
  login(credentials) {
    console.log('Login - Original password:', credentials.password);
    const encryptedPassword = encrypt(credentials.password);
    console.log('Login - Encrypted password:', encryptedPassword);
    return apiClient.post('/api/login', { ...credentials, password: encryptedPassword });
  },
  register(userData) {
    console.log('Register - Original password:', userData.password);
    const encryptedPassword = encrypt(userData.password);
    console.log('Register - Encrypted password:', encryptedPassword);

    return apiClient.post('/api/register', { ...userData, password: encryptedPassword });
  },
  getUserInfo() {
    return apiClient.get('/api/user').catch(error => {
      console.error('Error fetching user info:', error.response ? error.response.data : error.message);
      throw error;
    });
  },
  updateUserInfo(userData) {
    return apiClient.put('/api/user', userData);
  },
  getUsers() {
    return apiClient.get('/api/users');
  },
  deleteUser(userId) {
    return apiClient.delete(`/api/users/${userId}`);
  },
  getSettings() {
    return apiClient.get('/api/settings');
  },
  updateSettings(settings) {
    return apiClient.put('/api/settings', settings);
  },
  updatePassword(username, oldPassword, newPassword) {
    const encryptedOldPassword = encrypt(oldPassword);
    const encryptedNewPassword = encrypt(newPassword);
    return apiClient.put(`/api/user/password`, {
      username,
      oldPassword: encryptedOldPassword,
      newPassword: encryptedNewPassword
    });
  },
  getUserConfig() {
    return apiClient.get('/api/user/config');
  },
  createUser(data) {
    return apiClient.post('/api/user/create', data);
  },
  getAllUsers() {
    return apiClient.get('/api/users');
  },
  updateUser: async (userId, userData) => {
    return apiClient.put(`/api/users/${userId}`, userData);
  },
  getFormFields() {
    return apiClient.get('/api/settings/forms');
  },
  addFormField(data) {
    return apiClient.post('/api/settings/forms', data);
  },
  updateFormField(key, data) {
    return apiClient.put(`/api/settings/forms/${key}`, data);
  },
  deleteFormField(key) {
    return apiClient.delete(`/api/settings/forms/${key}`);
  },
  createOrder(orderData) {
    return apiClient.post('/api/orders', orderData);
  },
  getOrders(params) {
    return apiClient.get('/api/orders', { params });
  },
  updateOrderStatus(data) {
    return apiClient.put('/api/orders/status', data);
  },
  deleteOrder(id) {
    return apiClient.delete(`/api/orders/${id}`);
  },
  updateOrder(id, data) {
    return apiClient.put(`/api/orders/${id}`, data);
  },
  // 审核消息内容
  auditMessage(text) {
    return apiClient.post('/api/orders/audit-message', { text });
  },
  deleteUploadedFile(fileId) {
    return apiClient.delete(`/api/upload/${fileId}`);
  },
  // 自动回复相关API
  getAutoReplyRules(params) {
    return apiClient.get('/api/autoreplay', { params });
  },
  getAutoReplyStats() {
    return apiClient.get('/api/autoreplay/stats');
  },
  createAutoReplyRule(data) {
    return apiClient.post('/api/autoreplay', data);
  },
  updateAutoReplyRule(id, data) {
    return apiClient.put(`/api/autoreplay/${id}`, data);
  },
  deleteAutoReplyRule(id) {
    return apiClient.delete(`/api/autoreplay/${id}`);
  },
  previewAutoReply(data) {
    return apiClient.post('/api/bots/autoreplaypreview', data);
  },

  importAutoReplyRules(data) {
    return apiClient.post('/api/autoreplay/import', data);
  },
  // Telegram 登录
  loginTelegram(user) {
    return apiClient.post('/api/telegram', user);
  },
  // 群组相关 API
  getGroups(userid) {
    return apiClient.get('/api/groups', {
      params: { userid }
    });
  },
  deleteGroup(id) {
    return apiClient.delete(`/api/groups/${id}`);
  },
  getGroupById(groupId) {
    return apiClient.get(`/api/groups/${groupId}`);
  },
  updateUserGroup(userId, groupId) {
    return apiClient.put(`/api/users/${userId}/group`, { groupId });
  },
  updateGroupById(groupId, data) {
    return apiClient.put(`/api/groups/${groupId}/carouselconf`, data);
  },
  updatePointmangerconfById(groupId, data) {
    return apiClient.put(`/api/groups/${groupId}/pointmangerconf`, data);
  },
  // Dashboard 相关 API
  getDashboardStats(groupId) {
    return apiClient.get('/api/dashboard/stats', { params: { groupId } });
  },
  // 机器人管理相关API
  getBots(params) {
    return apiClient.get('/api/bots', { params });
  },
  createBot(data) {
    return apiClient.post('/api/bots', data);
  },
  updateBot(id, data) {
    return apiClient.put(`/api/bots/${id}`, data);
  },
  deleteBot(id, ownerid) {
    return apiClient.delete(`/api/bots/${id}`, { data: { ownerid } });
  },
  startBot(botId) {
    return apiClient.post(`/api/bots/${botId}/start`);
  },
  // 积分管理相关API
  getPointsManagerList(params) {
    return apiClient.get('/api/pointsManager', { params });
  },
  createPointsManager(data) {
    return apiClient.post('/api/pointsManager', data);
  },
  updatePointsManager(id, data) {
    return apiClient.put(`/api/pointsManager/${id}`, data);
  },
  deletePointsManager(id) {
    return apiClient.delete(`/api/pointsManager/${id}`);
  },
  importPointsManager(data) {
    return apiClient.post('/api/pointsManager/import', data);
  },
  exportPointsManager(params) {
    return apiClient.get('/api/pointsManager/export', { params });
  },
  // 积分日志相关API
  getPointHis(params) {
    return apiClient.get('/api/pointsManager/pointHis', { params });
  },
  // 轮播广告相关API
  getCarouselList(params) {
    return apiClient.get('/api/carousel', { params });
  },
  createCarousel(data) {
    return apiClient.post('/api/carousel', data);
  },
  updateCarousel(id, data) {
    return apiClient.put(`/api/carousel/${id}`, data);
  },
  deleteCarousel(id) {
    return apiClient.delete(`/api/carousel/${id}`);
  },
  importCarousel(data) {
    return apiClient.post('/api/carousel/import', data);
  },
  exportCarousel(params) {
    return apiClient.get('/api/carousel/export', { params });
  },
  previewCarousel(data) {
    return apiClient.post('/api/bots/carouselpreview', data);
  },
  // 邀请链接相关API
  getInviteLinksList(params) {
    return apiClient.get('/api/inviteLinks', { params });
  },
  createInviteLink(data) {
    return apiClient.post('/api/inviteLinks', data);
  },
  updateInviteLink(id, data) {
    return apiClient.put(`/api/inviteLinks/${id}`, data);
  },
  deleteInviteLink(id) {
    return apiClient.delete(`/api/inviteLinks/${id}`);
  },
  importInviteLinks(data) {
    return apiClient.post('/api/inviteLinks/import', data);
  },
  exportInviteLinks(params) {
    return apiClient.get('/api/inviteLinks/export', { params });
  },
  // 邀请用户相关API
  getInviteUsersList(params) {
    return apiClient.get('/api/inviteLinks/getInviteUsers', { params });
  },
  /////////导入邀请的用户列表
  importInviteUsers(data) {
    return apiClient.post('/api/inviteLinks/importUsers', data);
  },
  updateInviteUsers(id, data) {
    return apiClient.put(`/api/inviteLinks/updateInviteUsers/${id}`, data);
  },

  // 积分商品相关API
  getPointsGoodsList(params) {
    return apiClient.get('/api/pointsExchange/goods', { params });
  },
  createPointsGoods(data) {
    return apiClient.post('/api/pointsExchange/goods', data);
  },
  updatePointsGoods(id, data) {
    return apiClient.put(`/api/pointsExchange/goods/${id}`, data);
  },
  deletePointsGoods(id) {
    return apiClient.delete(`/api/pointsExchange/goods/${id}`);
  },
  importPointsGoods(data) {
    return apiClient.post('/api/pointsExchange/goods/import', data);
  },
  exportPointsGoods(params) {
    return apiClient.get('/api/pointsExchange/goods/export', { params });
  },
  updatePointsExchangeConfig(groupId, data) {
    return apiClient.put(`/api/groups/${groupId}/pointsExchangeconfig`, data);
  },
  pointsexchangepreview(data) {
    return apiClient.post('/api/bots/pointsexchangepreview', data);
  },

  // 积分兑换记录相关API
  getPointsExchangeHisList(params) {
    return apiClient.get('/api/pointsExchange/history', { params });
  },
  updatePointsExchangeHis(id, data) {
    return apiClient.put(`/api/pointsExchange/history/${id}`, data);
  },
  importPointsExchangeHistory(data) {
    return apiClient.post('/api/pointsExchange/history/import', data);
  },
  exportPointsExchangeHistory(params) {
    return apiClient.get('/api/pointsExchange/history/export', { params });
  },
  updateInviteConfig(groupId, data) {
    return apiClient.put(`/api/groups/${groupId}/inviteconfig`, data);
  },
  // 成员管理相关API
  getMemberManagerList(params) {
    return apiClient.get('/api/memberManager', { params });
  },
  importMemberManager(data) {
    return apiClient.post('/api/memberManager/import', data);
  },
  exportMemberManager(params) {
    return apiClient.get('/api/memberManager/export', { params });
  },

  // 禁言成员
  muteMember(data) {
    return apiClient.post('/api/memberManager/mute', data);
  },

  // 踢出成员
  kickMember(data) {
    return apiClient.post('/api/memberManager/kick', data);
  },

  // 修改成员积分（正数增加，负数减少）
  updateMemberPoint(data) {
    return apiClient.post('/api/memberManager/updatePoint', data);
  },

  // 获取用户积分日志
  getMemberPointLog(params) {
    return apiClient.get('/api/memberManager/pointLog', { params });
  },
  // 同步用户状态
  syncMemberStatus(data) {
    return apiClient.post('/api/memberManager/syncMemberStatus', data);
  },

  ///////////////////////积分骰子相关API
  updateDiceConfig(groupId, data) {
    return apiClient.put(`/api/groups/${groupId}/diceconfig`, data);
  },
  // 获取骰子游戏记录列表
  getDicepointHisList(params) {
    return apiClient.get('/api/dice/pointHis', { params });
  },
  getDiceuserHisList(params) {
    return apiClient.get('/api/dice/userHis', { params });
  },
  // 导入骰子游戏记录
  importDicepointHis(data) {
    return apiClient.post('/api/dice/pointHis/import', data);
  },
  // 导入骰子游戏用户记录
  importDiceuserHis(data) {
    return apiClient.post('/api/dice/userHis/import', data);
  },
  // 导出骰子游戏记录
  exportDicepointHis(params) {
    return apiClient.get('/api/dice/pointHis/export', { params });
  },
  dicepreview(data) {
    return apiClient.post('/api/bots/dicepreview', data);
  },
  openDicegame(id) {
    ////////使用update方式更新 openTimen列
    return apiClient.put(`/api/dice/pointHis/${id}`);
  },
  ////////////////////积分回复功能的api接口
  updateReplyPointsConfig(groupId, data) {
    return apiClient.put(`/api/groups/${groupId}/replypointsconfig`, data);
  }

};
